import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import moment from 'moment';
import { WAREHOUSE_ROUTES } from 'constants/routes';

interface ItemResponse {
  id: string;
  sku: string;
  status: string;
  quantity: number;
  description?: string;
  attachments: any[];
}

interface PossibleOrder {
  id: string;
  uuid: string;
  status: string;
  supplier?: {
    id: string;
    uuid?: string;
    name: string;
    status: string;
    verified?: boolean;
  };
  completedItemsPercentage?: number;
  itemsResponseList?: ItemResponse[];
  shipmentDate?: string;
  expectedArrivalDate?: string;
  attachmentIds?: string[];
}

export const usePossibleOrders = () => {
  const [possibleOrders, setPossibleOrders] = useState<PossibleOrder[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const ordersData = sessionStorage.getItem('possibleOrders');

    if (!ordersData) {
      message.error('No orders data found');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
      return;
    }

    try {
      const orders = JSON.parse(ordersData);
      setPossibleOrders(orders);
    } catch (error) {
      console.error('Error parsing orders data:', error);
      message.error('Failed to load orders data');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [navigate]);

  const handleSelectOrder = useCallback(
    async (order: PossibleOrder) => {
      try {
        // Note: Files were already uploaded by the identify-order API
        // We just need to navigate to the order details

        // Clear sessionStorage
        sessionStorage.removeItem('possibleOrders');
        sessionStorage.removeItem('deliveryNoteFiles');

        message.success('Order selected successfully');

        // Navigate to order details page
        navigate(`${WAREHOUSE_ROUTES.inboundPackages}/${order.id}`);
      } catch (error) {
        console.error('Error selecting order:', error);
        message.error('Failed to select order. Please try again.');
      }
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    // Clear sessionStorage
    sessionStorage.removeItem('possibleOrders');
    sessionStorage.removeItem('deliveryNoteFiles');
    navigate(WAREHOUSE_ROUTES.inboundPackages);
  }, [navigate]);

  const formatDate = useCallback((date: string) => {
    return moment(date).format('DD.MM.YYYY');
  }, []);

  return {
    possibleOrders,
    handleSelectOrder,
    handleBack,
    formatDate,
  };
};
